import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { ExportPdfColor } from "../../../../../root/theme/exportColor";
import eatprestoLogo from "../../../../../assets/images/logos/logo.png";
import _ from "lodash";

function isDarkColor(color: string): boolean {
  let r = 0,
    g = 0,
    b = 0;

  if (color.startsWith("rgb")) {
    const match = color.match(/rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    } else {
      return false;
    }
  } else if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      return false;
    }
  } else {
    return false;
  }

  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 128;
}

const rgbToHex = (r: number, g: number, b: number): string => {
  const clamp = (v: number) => Math.max(0, Math.min(255, v));
  const toHex = (v: number) => clamp(v).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const handleDownloadPdfNew = (
  columns: any,
  nodeData: any,
  filterDetails: any,
) => {
  const doc: any = new jsPDF("p");
  const totalPagesExp = "{total_pages_count_string}";
  const locationFilter = filterDetails.split("*")[0];
  const pageWidth = doc.internal.pageSize.getWidth();
  const today = moment().format("MMM_Do_YY").toString();
  let locationLength = 0;

  if (!locationFilter.includes(",")) {
    locationLength = 10;
  }

  /* Insert image */
  const img = new Image();
  img.src = eatprestoLogo;

  const commonTableOptions = {
    styles: {
      align: "center",
      fontSize: 8,
      valign: "middle",
      halign: "center",
      borderRadius: "10px",
    },
    columnStyles: {
      0: { halign: "left" }, // month column left
      1: { halign: "center" }, // Count column center
      2: { halign: "center" }, // Count column center
      3: { halign: "center" }, // Count column center
      4: { halign: "center" }, // Count column center
      5: { halign: "center" }, // Count column center
      6: { halign: "center" }, // Count column center
      7: { halign: "right" }, // Total column right
    },
    headStyles: {
      fillColor: [ExportPdfColor.HeaderRowColorLight],
      textColor: ["#000000"],
      fontSize: 7,
      valign: "middle",
      halign: "center",
      fontStyle: "bold",
    },
    didParseCell: (data: any) => {
      // Override header alignment per column
      if (data.section === "head") {
        if (data.column.index === 0) {
          data.cell.styles.halign = "left";
        } else if (data.column.index === 7) {
          data.cell.styles.halign = "right";
        } else {
          data.cell.styles.halign = "center";
        }
      }
      if (data.section === "body") {
        const isTotalRow = data.row.index === data.table.body.length - 1; // Last row
        if (isTotalRow) {
          data.cell.styles.fillColor = ExportPdfColor.green;
          data.cell.styles.textColor = "#ffffff";
          data.cell.styles.fontStyle = "bold"; // Bold font for total row
        } else if (data.row.index % 2 === 0) {
          data.cell.styles.fillColor = ExportPdfColor.EvenRowColorLight;
        } else {
          data.cell.styles.fillColor = ExportPdfColor.OddRowColorLight;
        }
      }
    },
    tableLineWidth: 0.1,
    margin: { top: 30, bottom: 30, left: 15, right: 15 },
    didDrawPage: function (data: any) {
      let pageNumber = "Page " + doc.internal.getNumberOfPages();
      doc.setFontSize(12);
      doc.text("Presto Express Solutions", data.settings.margin.left, 12);
      doc.text("Monthly Sales Report", data.settings.margin.left + 144, 12);

      doc.setFontSize(16);
      if (doc.internal.getNumberOfPages() === 1) {
        doc.setTextColor("#32363E");
        if (!locationFilter.includes(",")) {
          doc.text(
            filterDetails
              .split("*")[0]
              .replace("Discount summary report for location/s:", ""),
            pageWidth / 2,
            36,
            { align: "center" },
          );
        }

        doc.setTextColor(0, 0, 0);
        doc.text(
          "Monthly Sales Report",
          pageWidth / 2,
          36 + locationLength,
          "center",
        );
        doc.setFontSize(10);

        let filterLocationsDetails = "";
        if (locationFilter.split(",").length === 1) {
          filterLocationsDetails =
            "Monthly Sales Report for " + locationFilter.split(",")[0];
        } else {
          filterLocationsDetails =
            "Monthly Sales Report for " +
            locationFilter.split(",").length +
            " Locations";
        }

        doc.text(
          doc.splitTextToSize(filterLocationsDetails, 210 - 15 - 15),
          pageWidth / 2,
          44 + locationLength,
          { align: "center" },
        );

        doc.text(
          doc.splitTextToSize(
            "Generated for the period of " + filterDetails.split("*")[1],
            210 - 15 - 15,
          ),
          pageWidth / 2,
          50 + locationLength,
          { align: "center" },
        );
      }

      if (typeof doc.putTotalPages === "function") {
        pageNumber = pageNumber + " of " + totalPagesExp;
      }

      doc.setFontSize(10);
      let pageSize = doc.internal.pageSize;
      const pageHeight = pageSize.height
        ? pageSize.height
        : pageSize.getHeight();
      doc.text(pageNumber, data.settings.margin.left, pageHeight - 10);
      doc.addImage(
        img,
        "JPEG",
        data.settings.margin.left + 144,
        pageHeight - 24,
        40,
        16,
      );
    },
  };
  // Convert nodeData to table rows
  const tableRows = nodeData.map((row: any) => [
     row.month, // month column
    row.saleCount, 
    row.saleTotal.toFixed(2), 
    row.discount.toFixed(2), 
    row.total.toFixed(2), // Total column
    row.cash.toFixed(2), 
    row.card.toFixed(2), 
    row.otherPayments.toFixed(2), 
  ]);

  /* Create the table */
  /* Create the table */
  if (!_.isEmpty(nodeData)) {
    doc.setFontSize(16);
    doc.text("Monthly Sales Report", 82, 70);

    // Calculate equal width for all columns
    const tableWidth =
      pageWidth -
      commonTableOptions.margin.left -
      commonTableOptions.margin.right;
    const colWidth = tableWidth / columns.length;

    doc.autoTable({
      ...commonTableOptions,
      head: [columns], // ["month", "Count", "Total"]
      body: tableRows,
      startY: 65 + locationLength,
      columnStyles: {
        0: { halign: "left", cellWidth: colWidth }, // month column left
        1: { halign: "center", cellWidth: colWidth }, // Count column center
        2: { halign: "center", cellWidth: colWidth }, // Count column center
        3: { halign: "center", cellWidth: colWidth }, // Count column center
        4: { halign: "center", cellWidth: colWidth }, // Count column center
        5: { halign: "center", cellWidth: colWidth }, // Count column center
        6: { halign: "center", cellWidth: colWidth }, // Count column center
        7: { halign: "right", cellWidth: colWidth }, // Total column right
      },
    });
  }
  if (typeof doc.putTotalPages === "function") {
    doc.putTotalPages(totalPagesExp);
  }

  doc.save(
    `Monthly_Sales_Report_${today}_${Math.floor(
      100000 + Math.random() * 900000,
    )}.pdf`,
  );
};
